import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronRight, Info, Trophy } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Switch } from "@v2/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@v2/components/ui/tabs";
import { cn } from "@v2/lib/utils";
import { supabaseV2 } from "../../lib/supabase";
import { eaGameLabel } from "../../lib/ea-game";
import { playerRoleLabel } from "../../lib/labels";
import { EmptyState, SectionLoading } from "../../components/state";
import type { Team } from "../../types";
import {
  DEPT_CLASS,
  DEPT_ORDER,
  MIN_MATCHES_FOR_BEST,
  MIN_MATCHES_PER_ARCHETYPE,
  addRow,
  archetypeName,
  avgRating,
  deptOf,
  emptyTotals,
  pct,
  perMatch,
  sortArchetypes,
  type ArchetypeDept,
  type ArchetypeStatsPayload,
  type OppRow,
  type OursRow,
  type Totals,
} from "./archetypes";

type PlayerGroup = {
  key: string;
  playerId: string | null;
  name: string;
  eaName: string;
  roleCode: string | null;
  total: Totals;
  byArchetype: { archetype: number; totals: Totals }[];
  best: number | null;
};

export function ArchetypesView({
  team,
  onOpenPlayer,
}: {
  team: Team;
  onOpenPlayer: (playerId: string) => void;
}) {
  const { t } = useTranslation("players");

  const statsQuery = useQuery({
    queryKey: ["team-archetype-stats", team.id],
    enabled: !!team.ea_club_id,
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("team_archetype_stats", { p_team_id: team.id });
      if (error) throw error;
      return data as ArchetypeStatsPayload;
    },
  });

  const ours = statsQuery.data?.ours ?? [];
  const opponents = statsQuery.data?.opponents ?? [];

  // Giochi presenti nei dati, il piu' recente per primo. Di default quello
  // del club collegato, se ci sono partite.
  const games = useMemo(() => {
    const set = new Set<string>([...ours.map((r) => r.game), ...opponents.map((r) => r.game)]);
    return [...set].sort().reverse();
  }, [ours, opponents]);
  const [gameChoice, setGameChoice] = useState<string | null>(null);
  const game = gameChoice && games.includes(gameChoice)
    ? gameChoice
    : team.ea_game && games.includes(team.ea_game)
      ? team.ea_game
      : games[0] ?? null;

  const gameOurs = useMemo(() => ours.filter((r) => r.game === game), [ours, game]);
  const gameOpp = useMemo(() => opponents.filter((r) => r.game === game), [opponents, game]);

  if (!team.ea_club_id) {
    return (
      <div className="space-y-4 animate-fade-in">
        <Header />
        <EmptyState text={t("archetypes.noClub")} />
      </div>
    );
  }
  if (statsQuery.isLoading) return <SectionLoading />;
  if (statsQuery.error) {
    return (
      <div className="space-y-4 animate-fade-in">
        <Header />
        <EmptyState text={t("archetypes.loadError")} />
      </div>
    );
  }
  if (!game) {
    return (
      <div className="space-y-4 animate-fade-in">
        <Header />
        <EmptyState text={t("archetypes.noData")} />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <Header />

      <Card className="shadow-card">
        <CardContent className="p-4 space-y-3">
          {games.length > 1 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">{t("archetypes.game")}</p>
              <div className="flex flex-wrap gap-1.5">
                {games.map((g) => (
                  <Pill key={g} active={g === game} onClick={() => setGameChoice(g)}>
                    {eaGameLabel(g)}
                  </Pill>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-start gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-[11px] text-primary">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span className="leading-snug">
              {t("archetypes.note")}
              {game === "fc26" ? ` ${t("archetypes.noteFc26")}` : ""}
            </span>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="players" className="space-y-3">
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="players" className="flex-1 sm:flex-none">{t("archetypes.tabs.players")}</TabsTrigger>
          <TabsTrigger value="archetypes" className="flex-1 sm:flex-none">{t("archetypes.tabs.archetypes")}</TabsTrigger>
          <TabsTrigger value="opponents" className="flex-1 sm:flex-none">{t("archetypes.tabs.opponents")}</TabsTrigger>
        </TabsList>
        <TabsContent value="players">
          <PlayersTab rows={gameOurs} game={game} onOpenPlayer={onOpenPlayer} />
        </TabsContent>
        <TabsContent value="archetypes">
          <ArchetypesTab rows={gameOurs} game={game} />
        </TabsContent>
        <TabsContent value="opponents">
          <OpponentsTab rows={gameOpp} game={game} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Header() {
  const { t } = useTranslation("players");
  return (
    <section>
      <h2 className="text-2xl font-semibold">{t("archetypes.title")}</h2>
      <p className="text-sm text-muted-foreground">{t("archetypes.subtitle")}</p>
    </section>
  );
}

// --- Giocatori -------------------------------------------------------------

function PlayersTab({
  rows,
  game,
  onOpenPlayer,
}: {
  rows: OursRow[];
  game: string;
  onOpenPlayer: (playerId: string) => void;
}) {
  const { t } = useTranslation("players");
  const hasLinked = rows.some((r) => r.player_id);
  const [onlyRoster, setOnlyRoster] = useState(true);

  const groups = useMemo(() => {
    const map = new Map<string, PlayerGroup>();
    for (const r of rows) {
      const key = r.player_id ?? `ea:${r.playername.toLowerCase()}`;
      let g = map.get(key);
      if (!g) {
        g = {
          key,
          playerId: r.player_id,
          name: r.display_name || r.playername,
          eaName: r.playername,
          roleCode: r.role_code,
          total: emptyTotals(),
          byArchetype: [],
          best: null,
        };
        map.set(key, g);
      }
      addRow(g.total, r);
      const existing = g.byArchetype.find((a) => a.archetype === r.archetype);
      if (existing) addRow(existing.totals, r);
      else g.byArchetype.push({ archetype: r.archetype, totals: addRow(emptyTotals(), r) });
    }
    for (const g of map.values()) {
      g.byArchetype.sort((a, b) => b.totals.matches - a.totals.matches);
      // "Rende meglio con": solo se almeno due archetipi hanno un campione
      // sufficiente, altrimenti non c'e' confronto.
      const eligible = g.byArchetype.filter((a) => a.totals.matches >= MIN_MATCHES_PER_ARCHETYPE);
      if (eligible.length >= 2) {
        g.best = eligible.reduce((best, a) =>
          (avgRating(a.totals) ?? 0) > (avgRating(best.totals) ?? 0) ? a : best,
        ).archetype;
      }
    }
    return [...map.values()].sort((a, b) => b.total.matches - a.total.matches);
  }, [rows]);

  const visible = hasLinked && onlyRoster ? groups.filter((g) => g.playerId) : groups;

  return (
    <div className="space-y-3">
      {hasLinked && (
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Switch checked={onlyRoster} onCheckedChange={setOnlyRoster} />
          {t("archetypes.players.onlyRoster")}
        </label>
      )}
      {visible.length === 0 ? (
        <EmptyState text={t("archetypes.noData")} />
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {visible.map((g) => (
            <Card key={g.key} className="shadow-card">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    {g.playerId ? (
                      <button
                        type="button"
                        onClick={() => onOpenPlayer(g.playerId!)}
                        className="inline-flex items-center gap-1 font-semibold hover:underline text-left"
                      >
                        <span className="truncate">{g.name}</span>
                        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      </button>
                    ) : (
                      <p className="font-semibold truncate">{g.name}</p>
                    )}
                    <p className="text-xs text-muted-foreground truncate">
                      {[g.roleCode ? playerRoleLabel(g.roleCode) : null, g.name !== g.eaName ? g.eaName : null, !g.playerId ? t("archetypes.players.notInRoster") : null]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  <p className="shrink-0 text-xs text-muted-foreground tabular-nums">
                    {t("archetypes.matchesCount", { count: g.total.matches })}
                  </p>
                </div>

                <div className="overflow-x-auto -mx-1">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        <th className="text-left font-semibold px-1 pb-1">{t("archetypes.col.archetype")}</th>
                        <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.matches")}</th>
                        <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.rating")}</th>
                        <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.goalsPm")}</th>
                        <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.assistsPm")}</th>
                        <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.winPct")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.byArchetype.map(({ archetype, totals }) => {
                        const rating = avgRating(totals);
                        return (
                          <tr key={archetype} className="border-t">
                            <td className="px-1 py-1.5">
                              <span className="inline-flex items-center gap-1">
                                <ArchetypeChip id={archetype} game={game} />
                                {g.best === archetype && (
                                  <Trophy className="h-3.5 w-3.5 text-amber-500" aria-label={t("archetypes.players.best")} />
                                )}
                              </span>
                            </td>
                            <td className="px-1 py-1.5 text-right tabular-nums">{totals.matches}</td>
                            <td className="px-1 py-1.5 text-right tabular-nums font-medium">{rating != null ? rating.toFixed(2) : "—"}</td>
                            <td className="px-1 py-1.5 text-right tabular-nums">{perMatch(totals.goals, totals.matches).toFixed(2)}</td>
                            <td className="px-1 py-1.5 text-right tabular-nums">{perMatch(totals.assists, totals.matches).toFixed(2)}</td>
                            <td className="px-1 py-1.5 text-right tabular-nums">{fmtPct(pct(totals.wins, totals.matches))}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {g.best != null && (
                  <p className="text-xs text-muted-foreground">
                    <Trophy className="inline h-3.5 w-3.5 -mt-0.5 mr-1 text-amber-500" />
                    {t("archetypes.players.bestWith", { name: archetypeName(g.best, game), min: MIN_MATCHES_PER_ARCHETYPE })}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Archetipi ---------------------------------------------------------------

function ArchetypesTab({ rows, game }: { rows: OursRow[]; game: string }) {
  const { t } = useTranslation("players");
  const [role, setRole] = useState<string>("all");

  const roles = useMemo(
    () => [...new Set(rows.map((r) => r.role_code).filter((r): r is string => !!r))].sort(),
    [rows],
  );

  const byArchetype = useMemo(() => {
    const scoped = role === "all" ? rows : rows.filter((r) => r.role_code === role);
    const map = new Map<number, { totals: Totals; players: Set<string> }>();
    for (const r of scoped) {
      const e = map.get(r.archetype) ?? { totals: emptyTotals(), players: new Set<string>() };
      addRow(e.totals, r);
      e.players.add(r.player_id ?? r.playername.toLowerCase());
      map.set(r.archetype, e);
    }
    return map;
  }, [rows, role]);

  // Migliore per reparto: voto medio piu' alto con un campione sufficiente.
  const bestByDept = useMemo(() => {
    const best = new Map<ArchetypeDept | "other", number>();
    for (const [id, e] of byArchetype) {
      if (e.totals.matches < MIN_MATCHES_FOR_BEST) continue;
      const d = deptOf(id, game);
      const current = best.get(d);
      if (current == null || (avgRating(e.totals) ?? 0) > (avgRating(byArchetype.get(current)!.totals) ?? 0)) {
        best.set(d, id);
      }
    }
    return best;
  }, [byArchetype, game]);

  const ids = sortArchetypes([...byArchetype.keys()], game);
  const groups = [...DEPT_ORDER, "other" as const]
    .map((d) => ({ dept: d, ids: ids.filter((id) => deptOf(id, game) === d) }))
    .filter((g) => g.ids.length > 0);

  return (
    <div className="space-y-3">
      {roles.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">{t("archetypes.archetypes.roleFilter")}</p>
          <div className="flex flex-wrap gap-1.5">
            <Pill active={role === "all"} onClick={() => setRole("all")}>{t("archetypes.archetypes.allRoles")}</Pill>
            {roles.map((r) => (
              <Pill key={r} active={role === r} onClick={() => setRole(r)}>
                <span title={playerRoleLabel(r)}>{r}</span>
              </Pill>
            ))}
          </div>
        </div>
      )}

      {groups.length === 0 ? (
        <EmptyState text={t("archetypes.noData")} />
      ) : (
        groups.map(({ dept, ids: deptIds }) => (
          <Card key={dept} className="shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t(`archetypes.dept.${dept}`)}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="overflow-x-auto -mx-1">
                <table className="w-full text-sm min-w-[520px]">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="text-left font-semibold px-1 pb-1">{t("archetypes.col.archetype")}</th>
                      <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.matches")}</th>
                      <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.players")}</th>
                      <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.rating")}</th>
                      <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.goalsPm")}</th>
                      <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.assistsPm")}</th>
                      <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.passPct")}</th>
                      <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.tacklesPm")}</th>
                      <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.winPct")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deptIds.map((id) => {
                      const e = byArchetype.get(id)!;
                      const rating = avgRating(e.totals);
                      const isBest = bestByDept.get(dept) === id;
                      return (
                        <tr key={id} className="border-t">
                          <td className="px-1 py-1.5">
                            <span className="inline-flex items-center gap-1">
                              <ArchetypeChip id={id} game={game} />
                              {isBest && <Trophy className="h-3.5 w-3.5 text-amber-500" aria-label={t("archetypes.archetypes.best")} />}
                            </span>
                          </td>
                          <td className="px-1 py-1.5 text-right tabular-nums">{e.totals.matches}</td>
                          <td className="px-1 py-1.5 text-right tabular-nums">{e.players.size}</td>
                          <td className="px-1 py-1.5 text-right tabular-nums font-medium">{rating != null ? rating.toFixed(2) : "—"}</td>
                          <td className="px-1 py-1.5 text-right tabular-nums">{perMatch(e.totals.goals, e.totals.matches).toFixed(2)}</td>
                          <td className="px-1 py-1.5 text-right tabular-nums">{perMatch(e.totals.assists, e.totals.matches).toFixed(2)}</td>
                          <td className="px-1 py-1.5 text-right tabular-nums">{fmtPct(pct(e.totals.passes_made, e.totals.pass_attempts))}</td>
                          <td className="px-1 py-1.5 text-right tabular-nums">{perMatch(e.totals.tackles_made, e.totals.matches).toFixed(1)}</td>
                          <td className="px-1 py-1.5 text-right tabular-nums">{fmtPct(pct(e.totals.wins, e.totals.matches))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))
      )}
      <p className="text-xs text-muted-foreground">
        <Trophy className="inline h-3.5 w-3.5 -mt-0.5 mr-1 text-amber-500" />
        {t("archetypes.archetypes.bestNote", { min: MIN_MATCHES_FOR_BEST })}
      </p>
    </div>
  );
}

// --- Avversari ---------------------------------------------------------------

function OpponentsTab({ rows, game }: { rows: OppRow[]; game: string }) {
  const { t } = useTranslation("players");
  const totalAppearances = rows.reduce((s, r) => s + r.appearances, 0);
  const sorted = [...rows].sort((a, b) => b.appearances - a.appearances);

  // Contro chi si vince meno: solo archetipi incontrati abbastanza spesso.
  const toughest = [...rows]
    .filter((r) => r.matches >= 10)
    .sort((a, b) => (pct(a.wins, a.matches) ?? 0) - (pct(b.wins, b.matches) ?? 0))[0];

  if (rows.length === 0) return <EmptyState text={t("archetypes.noData")} />;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <Highlight
          label={t("archetypes.opponents.mostFaced")}
          value={archetypeName(sorted[0].archetype, game)}
          hint={t("archetypes.opponents.shareHint", { pct: pct(sorted[0].appearances, totalAppearances) ?? 0 })}
        />
        {toughest && (
          <Highlight
            label={t("archetypes.opponents.toughest")}
            value={archetypeName(toughest.archetype, game)}
            hint={t("archetypes.opponents.toughestHint", {
              pct: pct(toughest.wins, toughest.matches) ?? 0,
              count: toughest.matches,
            })}
          />
        )}
      </div>
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t("archetypes.opponents.title")}</CardTitle>
          <CardDescription>{t("archetypes.opponents.desc")}</CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm min-w-[480px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left font-semibold px-1 pb-1">{t("archetypes.col.archetype")}</th>
                  <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.share")}</th>
                  <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.rating")}</th>
                  <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.goalsPa")}</th>
                  <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.record")}</th>
                  <th className="text-right font-semibold px-1 pb-1">{t("archetypes.col.winPct")}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.archetype} className="border-t">
                    <td className="px-1 py-1.5"><ArchetypeChip id={r.archetype} game={game} /></td>
                    <td className="px-1 py-1.5 text-right tabular-nums">{fmtPct(pct(r.appearances, totalAppearances))}</td>
                    <td className="px-1 py-1.5 text-right tabular-nums font-medium">{r.avg_rating != null ? Number(r.avg_rating).toFixed(2) : "—"}</td>
                    <td className="px-1 py-1.5 text-right tabular-nums">{perMatch(r.goals, r.appearances).toFixed(2)}</td>
                    <td className="px-1 py-1.5 text-right tabular-nums whitespace-nowrap">{r.wins}-{r.draws}-{r.losses}</td>
                    <td className="px-1 py-1.5 text-right tabular-nums">{fmtPct(pct(r.wins, r.matches))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// --- Pezzi comuni -------------------------------------------------------------

function ArchetypeChip({ id, game }: { id: number; game: string }) {
  return (
    <span className={cn("inline-flex items-center rounded-md border px-1.5 py-0.5 text-xs font-medium whitespace-nowrap", DEPT_CLASS[deptOf(id, game)])}>
      {archetypeName(id, game)}
    </span>
  );
}

function Highlight({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-xl border bg-card px-3 py-2.5">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold leading-tight">{label}</p>
      <p className="text-sm font-semibold mt-1 truncate">{value}</p>
      <p className="text-[10.5px] text-muted-foreground mt-0.5">{hint}</p>
    </div>
  );
}

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-smooth",
        active ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

const fmtPct = (v: number | null) => (v == null ? "—" : `${v}%`);
